import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { t } from '../stores/languageStore';
import { roomApi } from '../utils/roomApi';
import { Room } from '../utils/roomApi';
import { useNavigate } from 'react-router-dom';
import { Lock, Users, Calendar, DollarSign, ArrowRight, Building2 } from 'lucide-react';
import RoomPaymentModal from '../components/RoomPaymentModal';

const RoomListPage: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const navigate = useNavigate();

  const handlePaymentSuccess = () => {
    if (selectedRoom) {
      navigate(`/rooms/${selectedRoom.id}`);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await roomApi.getRooms();
      if (response.success && response.data) {
        setRooms(response.data);
      }
    } catch (error) {
      console.error('获取房间列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterRoom = async (room: Room) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      console.log('handleEnterRoom called, roomId:', room.id);
      const response = await roomApi.checkMembership(room.id);
      console.log('checkMembership response:', response);
      
      if (response.success && response.data && response.data.is_member) {
        console.log('User is member, navigating to room:', room.id);
        navigate(`/rooms/${room.id}`);
      } else {
        console.log('User is not member, showing payment modal');
        console.log('is_member value:', response.data?.is_member);
        console.log('is_admin value:', response.data?.is_admin);
        console.log('is_owner value:', response.data?.is_owner);
        setSelectedRoom(room);
        setShowPaymentModal(true);
      }
    } catch (error: any) {
      console.error('检查会员资格失败:', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const getPriceText = (room: Room) => {
    const price = Number(room.price).toFixed(2);
    switch (room.price_type) {
      case 'monthly':
        return `${price}${t('room.perMonth')}`;
      case 'yearly':
        return `${price}${t('room.perYear')}`;
      case 'one_time':
        return `${price}${t('room.permanentShort')}`;
      default:
        return `${price}`;
    }
  };

  const getPriceTypeLabel = (type: string) => {
    switch (type) {
      case 'monthly':
        return t('room.monthly');
      case 'yearly':
        return t('room.yearly');
      case 'one_time':
        return t('room.oneTime');
      default:
        return type;
    }
  };

  const getPriceTypeColor = (type: string) => {
    switch (type) {
      case 'monthly':
        return 'bg-blue-100 text-blue-700';
      case 'yearly':
        return 'bg-green-100 text-green-700';
      case 'one_time':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('room.title')}</h1>
            <p className="text-gray-500 text-xs">{t('room.description_')}</p>
          </div>
        </div>
        {isAuthenticated && user && ['房主', '管理员', '站长', '超级管理员'].includes(user.role || '') && (
          <button
            onClick={() => navigate('/room-management')}
            className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-xs font-medium"
          >
            <Building2 className="w-3 h-3 mr-1.5" />
            {t('room.manage')}
          </button>
        )}
      </div>

      {/* 房间列表 */}
      <div className="space-y-2">
        {rooms.length === 0 ? (
          <div className="text-center py-12">
            <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">{t('room.noRooms')}</p>
            {isAuthenticated && user?.role === '房主' && (
              <button
                onClick={() => navigate('/room-management')}
                className="mt-3 inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
              >
                <Building2 className="w-3 h-3 mr-1.5" />
                {t('room.create')}
              </button>
            )}
          </div>
        ) : (
          rooms.map((room) => (
            <div
              key={room.id}
              className="flex items-center p-3 bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md hover:border-primary-200 transition-all duration-200 cursor-pointer group"
              onClick={() => handleEnterRoom(room)}
            >
              {/* 图标 */}
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {room.name.charAt(0)}
              </div>
              
              {/* 房间名称 */}
              <div className="ml-3 flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 text-sm truncate">{room.name}</span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getPriceTypeColor(room.price_type)} flex-shrink-0`}>
                    {getPriceTypeLabel(room.price_type)}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                  <span className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    {room.owner_name || t('common.unknown')}
                  </span>
                  <span className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {room.created_at ? new Date(room.created_at).toLocaleDateString('zh-CN') : '-'}
                  </span>
                </div>
              </div>

              {/* 价格和状态 */}
              <div className="ml-4 flex items-center gap-4 flex-shrink-0">
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <DollarSign className="w-3 h-3 text-primary-500" />
                    <span className="font-semibold text-primary-600 text-sm">USDT {getPriceText(room)}</span>
                  </div>
                  <span className={`text-xs mt-0.5 block ${room.is_active === 1 ? 'text-green-500' : 'text-gray-300'}`}>
                    {room.is_active === 1 ? t('room.running') : t('room.closed')}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          ))
        )}
      </div>

      {/* 支付弹窗 */}
      <RoomPaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedRoom(null);
        }}
        onSuccess={handlePaymentSuccess}
        roomName={selectedRoom?.name || ''}
        roomDescription={selectedRoom?.description}
        amount={Number(selectedRoom?.price) || 0}
        priceType={selectedRoom?.price_type || 'monthly'}
        roomId={selectedRoom?.id || 0}
        trc20Address={selectedRoom?.trc20_address}
        erc20Address={selectedRoom?.erc20_address}
      />
    </div>
  );
};

export default RoomListPage;